import React from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';

const {FiGift, FiCheckCircle, FiClock, FiPlus, FiExternalLink} = FiIcons;

const OffersWidget = () => {
  const {data, dataYear, dataAvailableYears, setDataYear} = useAuth0Data();
  const {offers} = data;

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = [...new Set([...dataAvailableYears, currentYear])].sort((a, b) => b - a);

  const handleYearChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDataYear(parseInt(e.target.value));
  };

  const activeOffers = offers.filter(offer => offer.status === 'active');
  const ideationOffers = offers.filter(offer => offer.status === 'ideation');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const totalActiveValue = activeOffers.reduce((sum, offer) => sum + offer.value, 0);
  const totalIdeationValue = ideationOffers.reduce((sum, offer) => sum + offer.value, 0);

  if (offers.length === 0) {
    return (
      <Link to="/data-management/offers" className="block">
        <motion.div
          whileHover={{y: -2}}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiGift} className="w-5 h-5 text-orange-600" />
              </div>
              <div className="ml-3">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">Offers</h3>
                  <select
                    value={dataYear}
                    onChange={handleYearChange}
                    onClick={(e) => e.preventDefault()}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-medium bg-white cursor-pointer"
                  >
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-orange-600" />
            </div>
          </div>
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiPlus} className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No offers found for {dataYear}.</p>
            <p className="text-sm">Click to add offers</p>
          </div>
        </motion.div>
      </Link>
    );
  }

  return (
    <Link to="/data-management/offers" className="block">
      <motion.div
        whileHover={{y: -2}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiGift} className="w-5 h-5 text-orange-600" />
            </div>
            <div className="ml-3">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">Offers</h3>
                <select
                  value={dataYear}
                  onChange={handleYearChange}
                  onClick={(e) => e.preventDefault()}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-medium bg-white cursor-pointer"
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <p className="text-sm text-gray-500">{offers.length} offers</p>
            </div>
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-orange-600" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-600">{activeOffers.length}</p>
            <p className="text-sm text-gray-600">Active Offers</p>
            <p className="text-xs text-gray-500 mt-1">{formatCurrency(totalActiveValue)}</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <SafeIcon icon={FiClock} className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-600">{ideationOffers.length}</p>
            <p className="text-sm text-gray-600">In Ideation</p>
            <p className="text-xs text-gray-500 mt-1">{formatCurrency(totalIdeationValue)}</p>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Recent Offers</h4>
          {offers.slice(0, 4).map((offer, index) => (
            <motion.div
              key={offer.id}
              initial={{opacity: 0, y: 10}}
              animate={{opacity: 1, y: 0}}
              transition={{delay: index * 0.1}}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div>
                <p className="font-medium text-gray-900 text-sm">{offer.title}</p>
                <p className="text-xs text-gray-500">{offer.client}</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900 text-sm">{formatCurrency(offer.value)}</p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  offer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {offer.status === 'active' ? 'Active' : 'Ideation'}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="text-center">
            <p className="text-sm text-gray-500">Total Pipeline Value</p>
            <p className="text-2xl font-bold text-orange-600">
              {formatCurrency(totalActiveValue + totalIdeationValue)}
            </p>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default OffersWidget;